import { DEFAULT_TIME_ZONE, addDays, comparePlainDate, daysBetween, wallTimeToInstant, weekdayOf, } from './time-zone.js';
export const otherParent = (parent) => (parent === 'A' ? 'B' : 'A');
/**
 * Cycles exprimés en jours, tels que les rédigent les conventions parentales françaises.
 * Chaque cycle est décrit une fois ici : ajouter un schéma ne demande pas de nouveau code.
 */
const CYCLES = {
    // Semaine alternée : sept jours chacun.
    alternating_week: [
        { days: 7, side: 'self' },
        { days: 7, side: 'other' },
    ],
    // 2-2-5-5 : deux jours chacun en semaine, puis cinq jours chacun englobant le week-end.
    '2255': [
        { days: 2, side: 'self' },
        { days: 2, side: 'other' },
        { days: 5, side: 'self' },
        { days: 5, side: 'other' },
    ],
    // 2-2-3 : les jours de semaine restent fixes, seul le week-end alterne.
    '223': [
        { days: 2, side: 'self' },
        { days: 2, side: 'other' },
        { days: 3, side: 'self' },
        { days: 2, side: 'other' },
        { days: 2, side: 'self' },
        { days: 3, side: 'other' },
    ],
    // 3-4-4-3 : même équilibre que le 2-2-5-5, avec une bascule en milieu de semaine.
    '3443': [
        { days: 3, side: 'self' },
        { days: 4, side: 'other' },
        { days: 4, side: 'self' },
        { days: 3, side: 'other' },
    ],
    // Quinzaine alternée : deux semaines chacun.
    fortnight: [
        { days: 14, side: 'self' },
        { days: 14, side: 'other' },
    ],
    // Alternances inégalitaires : le cycle reste de quatorze jours, le partage non.
    '95': [
        { days: 9, side: 'self' },
        { days: 5, side: 'other' },
    ],
    '104': [
        { days: 10, side: 'self' },
        { days: 4, side: 'other' },
    ],
};
/**
 * Schémas construits autour du week-end plutôt qu'autour d'un cycle en jours : la
 * résidence est principale chez un parent, l'autre reçoit l'enfant un week-end sur deux.
 * Leur seule différence est l'amplitude du week-end confié.
 */
const WEEKEND_DEFAULTS = {
    // Droit de visite classique : du vendredi soir au dimanche soir.
    alternate_weekends: { start: 5, end: 7 },
    // Droit de visite élargi : jusqu'au lundi matin, dépose à l'école incluse.
    extended_weekends: { start: 5, end: 1 },
};
export class UnsupportedScheduleError extends Error {
    constructor(type) {
        super(`Schéma de garde « ${type} » sans génération automatique : ` +
            'il doit être décrit par des exceptions explicites.');
        this.name = 'UnsupportedScheduleError';
    }
}
/**
 * Périodes de garde couvrant l'intervalle demandé.
 *
 * Les périodes débordent volontairement des bornes : une garde commencée avant `from`
 * et finissant après doit apparaître entière, sans quoi la question « qui a l'enfant
 * aujourd'hui ? » n'aurait pas de réponse en début d'intervalle.
 */
export function generateOccurrences(config, range) {
    if (comparePlainDate(range.from, range.to) > 0) {
        throw new RangeError('Intervalle inversé : la date de fin précède la date de début.');
    }
    const weekend = WEEKEND_DEFAULTS[config.type];
    return weekend ? generateAlternateWeekends(config, weekend, range) : generateCyclic(config, range);
}
function generateCyclic(config, range) {
    const cycle = CYCLES[config.type];
    if (!cycle)
        throw new UnsupportedScheduleError(config.type);
    const timeZone = config.timeZone ?? DEFAULT_TIME_ZONE;
    const cycleLength = cycle.reduce((total, segment) => total + segment.days, 0);
    // On recule au début du cycle qui couvre `from`, y compris si l'intervalle demandé
    // précède la date de début du schéma (l'arrondi vers le bas doit rester correct
    // pour les décalages négatifs, d'où le Math.floor plutôt qu'une division entière).
    const offset = daysBetween(config.startsOn, range.from);
    const cyclesToSkip = Math.floor(offset / cycleLength);
    const occurrences = [];
    let dayCursor = cyclesToSkip * cycleLength;
    const lastDay = daysBetween(config.startsOn, range.to) + 1;
    while (dayCursor < lastDay) {
        for (const segment of cycle) {
            const startDate = addDays(config.startsOn, dayCursor);
            const endDate = addDays(config.startsOn, dayCursor + segment.days);
            occurrences.push({
                start: wallTimeToInstant(startDate, config.handoverTime, timeZone),
                end: wallTimeToInstant(endDate, config.handoverTime, timeZone),
                parent: segment.side === 'self' ? config.startingParent : otherParent(config.startingParent),
            });
            dayCursor += segment.days;
            if (dayCursor >= lastDay)
                break;
        }
    }
    return occurrences;
}
/**
 * Week-ends alternés « 1er, 3e et 5e week-end du mois » — la formulation classique des
 * jugements français. Le rang du week-end se compte sur son samedi, dans le mois de ce
 * samedi : un week-end à cheval sur deux mois appartient donc au mois de son samedi,
 * ce qui évite les mois à deux « premiers week-ends ».
 */
function generateAlternateWeekends(config, defaults, range) {
    const timeZone = config.timeZone ?? DEFAULT_TIME_ZONE;
    const weekendStart = config.weekendStartsOn ?? defaults.start;
    const weekendEnd = config.weekendEndsOn ?? defaults.end;
    const returnTime = config.weekendReturnTime ?? config.handoverTime;
    const visiting = otherParent(config.startingParent);
    const occurrences = [];
    const scanFrom = addDays(range.from, -8);
    const scanTo = addDays(range.to, 8);
    // La continuité se raisonne en instants, pas en dates : le retour du dimanche a lieu à
    // 18h et la semaine reprend à cet instant précis, pas à l'heure de passage habituelle.
    let cursor = wallTimeToInstant(scanFrom, config.handoverTime, timeZone);
    for (let day = scanFrom; comparePlainDate(day, scanTo) <= 0; day = addDays(day, 1)) {
        if (weekdayOf(day) !== 6 || !isOddWeekendOfMonth(day))
            continue;
        const start = wallTimeToInstant(addDays(day, weekendStart - 6), config.handoverTime, timeZone);
        const end = wallTimeToInstant(addDays(day, daysAfterSaturday(weekendEnd)), returnTime, timeZone);
        if (end.getTime() <= cursor.getTime())
            continue;
        if (cursor.getTime() < start.getTime()) {
            occurrences.push({ start: cursor, end: start, parent: config.startingParent });
        }
        occurrences.push({ start, end, parent: visiting });
        cursor = end;
    }
    const scanEnd = wallTimeToInstant(scanTo, config.handoverTime, timeZone);
    if (cursor.getTime() < scanEnd.getTime()) {
        occurrences.push({ start: cursor, end: scanEnd, parent: config.startingParent });
    }
    return occurrences;
}
/**
 * Décalage, en jours après le samedi, du jour de retour. Le samedi vaut 6 et le dimanche 7
 * dans la numérotation ISO : les jours de la semaine suivante (lundi = 1) tombent donc
 * après, pas avant.
 */
function daysAfterSaturday(weekday) {
    return weekday >= 6 ? weekday - 6 : weekday + 1;
}
/** Rang du week-end dans son mois, compté sur le samedi. Vrai pour les 1er, 3e et 5e. */
function isOddWeekendOfMonth(saturday) {
    const rank = Math.floor((saturday.day - 1) / 7) + 1;
    return rank % 2 === 1;
}
//# sourceMappingURL=schedule.js.map